import { useState, useEffect } from "react";
import "@/App.css";
import { 
  Phone, 
  Mail, 
  Menu, 
  X, 
  Clock, 
  CheckCircle2, 
  FileText, 
  Wrench, 
  Zap, 
  Shield, 
  Users, 
  ChevronRight,
  MessageCircle,
  ExternalLink,
  ClipboardCheck,
  Settings,
  BarChart3,
  FileCheck,
  Cookie,
  FileDown,
  AlertTriangle
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Logo URL from user upload
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dce45770-8e08-41a3-a335-b1ec3da5a62a/artifacts/523c7omu_ETHIDA%202%20BLACK%20Gro%C3%9F.png";

// Timeline Data
const timelineSteps = [
  {
    step: 1,
    title: "Anfrage & Terminvereinbarung",
    duration: "1-2 Tage",
    description: "Was passiert:",
    points: [
      "Sie kontaktieren uns per WhatsApp, Telefon oder E-Mail",
      "Wir besprechen kurz Ihre Anlage (Größe, Komponenten, Standort)",
      "Gemeinsam finden wir einen passenden Termin",
      "Sie erhalten eine Terminbestätigung mit Vorbereitungs-Checkliste"
    ],
    requirements: {
      title: "Was wir von Ihnen brauchen:",
      items: [
        "Angaben zur Anlage (kWp, Modulanzahl, Wechselrichter-Typ)",
        "Adresse des Installationsorts",
        "Wunschtermin (Werktags, ca. 2-3 Stunden einplanen)"
      ]
    },
    icon: Phone
  },
  {
    step: 2,
    title: "Vorbereitung Ihrerseits",
    duration: "Vor dem Termin",
    description: "Ihre Aufgaben:",
    points: [
      "Dokumente zusammenstellen (Datenblätter, Stringplan, Rechnungen)",
      "Zugang zu allen Anlagenteilen sicherstellen (Dach, Keller, Zählerschrank)",
      "Leiter bereitstellen (falls Dachzugang nötig)",
      "Anlage sollte komplett montiert sein (wir prüfen, nicht installieren)"
    ],
    tip: "Nutzen Sie unsere Checkliste für Selbstbauer – dort finden Sie alle Details zur Vorbereitung.",
    icon: ClipboardCheck
  },
  {
    step: 3,
    title: "Ankunft & Besprechung",
    duration: "ca. 15 Minuten",
    description: "Was passiert:",
    points: [
      "Wir kommen zum vereinbarten Termin zu Ihnen",
      "Kurze Begrüßung und Vorstellung des Ablaufs",
      "Sie zeigen uns die Anlage und erklären, wer was installiert hat",
      "Wir sichten gemeinsam Ihre Dokumentation",
      "Offene Fragen werden direkt geklärt"
    ],
    goal: "Wir möchten verstehen, was Sie gebaut haben – und worauf wir besonders achten sollten.",
    icon: Users
  },
  {
    step: 4,
    title: "Sichtprüfung der Anlage",
    duration: "ca. 30-45 Minuten",
    description: "Was wir prüfen:",
    sections: [
      {
        title: "Auf dem Dach / an den Modulen:",
        items: [
          "Korrekte Montage und Befestigung der Module",
          "Zustand der Module (keine Beschädigungen, Mikrorisse)",
          "Kabelführung und UV-Schutz der Leitungen",
          "Steckverbindungen (MC4) – fest eingerastet, trocken, nicht unter Zug",
          "Erdung / Potentialausgleich der Modulrahmen"
        ]
      },
      {
        title: "Am Wechselrichter:",
        items: [
          "Korrekte Montage (Belüftung, Zugänglichkeit)",
          "DC-Eingänge richtig angeschlossen",
          "AC-Ausgang fachgerecht verdrahtet",
          "Überspannungsschutz vorhanden"
        ]
      },
      {
        title: "Im Zählerschrank:",
        items: [
          "Leitungsschutzschalter korrekt dimensioniert",
          "Fehlerstromschutzschalter (RCD) vorhanden",
          "Zählerplatz vorbereitet oder Zähler montiert",
          "Beschriftung aller Komponenten"
        ]
      },
      {
        title: "Sicherheitsaspekte:",
        items: [
          "Warnhinweise \"PV-Anlage\" angebracht",
          "DC-Freischalter vorhanden und erreichbar",
          "Feuerwehr-Kennzeichnung (bei größeren Anlagen)"
        ]
      }
    ],
    icon: Wrench
  },
  {
    step: 5,
    title: "Messungen nach VDE",
    duration: "ca. 30-45 Minuten",
    description: "Folgende Messungen führen wir durch:",
    sections: [
      {
        title: "DC-Seite (Gleichstrom):",
        items: [
          "Leerlaufspannung (Uoc) jedes Strings",
          "Kurzschlussstrom (Isc) jedes Strings",
          "Isolationswiderstand der DC-Leitungen",
          "Prüfung auf Erdschluss"
        ]
      },
      {
        title: "AC-Seite (Wechselstrom):",
        items: [
          "Netzimpedanz (Zi)",
          "Schleifenimpedanz",
          "Fehlerstrom-Schutzeinrichtung (RCD-Auslösetest)",
          "Isolationswiderstand"
        ]
      },
      {
        title: "Schutzmaßnahmen:",
        items: [
          "Durchgangsprüfung des Potentialausgleichs",
          "Prüfung der Schutzleiterverbindungen"
        ]
      }
    ],
    note: "Alle Messwerte werden dokumentiert und mit den Sollwerten verglichen.",
    icon: BarChart3
  },
  {
    step: 6,
    title: "Inbetriebnahme & Funktionstest",
    duration: "ca. 15-30 Minuten",
    description: "Was passiert:",
    points: [
      "DC-Seite wird zugeschaltet (Module → Wechselrichter)",
      "AC-Seite wird zugeschaltet (Wechselrichter → Netz)",
      "Wechselrichter startet und synchronisiert sich mit dem Netz",
      "Wir prüfen die Anzeigen: Leistung, Spannung, Einspeisestart",
      "Kommunikation wird getestet (WLAN, App-Anbindung falls vorhanden)",
      "Bei Speicher: Lade-/Entladefunktion wird geprüft"
    ],
    success: "Die Anlage speist Strom ins Netz ein und alle Werte liegen im Normbereich.",
    icon: Zap
  },
  {
    step: 7,
    title: "Dokumentation & Protokoll",
    duration: "ca. 15-20 Minuten",
    description: "Was Sie erhalten:",
    points: [
      "Inbetriebnahmeprotokoll mit allen Messwerten",
      "Prüfprotokoll nach VDE 0100-600 / VDE 0126",
      "Anlagendokumentation (Fotos, Stringplan, Komponentenliste)",
      "Mängelliste (falls Mängel vorhanden – mit Lösungsvorschlägen)"
    ],
    purpose: {
      title: "Diese Dokumente brauchen Sie für:",
      items: [
        "Die Anmeldung beim Netzbetreiber",
        "Die Registrierung im Marktstammdatenregister",
        "Ihre Versicherung",
        "Eventuelle Garantieansprüche"
      ]
    },
    icon: FileText
  },
  {
    step: 8,
    title: "Kundeneinweisung & Übergabe",
    duration: "ca. 20-30 Minuten",
    description: "Was wir Ihnen erklären:",
    points: [
      "Wie funktioniert Ihre Anlage? (Einfach verständlich)",
      "Wo sehen Sie Ihre Erträge? (App, Display, Portal)",
      "Was bedeuten die Anzeigen am Wechselrichter?",
      "Wie verhalten Sie sich bei einer Störung?",
      "Wie lesen Sie Ihren Stromzähler richtig ab?",
      "Tipps zur Eigenverbrauchsoptimierung"
    ],
    storage: {
      title: "Bei Speicher zusätzlich:",
      items: [
        "Lade-/Entladezyklen verstehen",
        "Notstromfunktion (falls vorhanden)",
        "Winterbetrieb und Speicherpflege"
      ]
    },
    conclusion: "Sie erhalten alle Dokumente, wir beantworten Ihre letzten Fragen – und dann gehört die Anlage ganz Ihnen.",
    icon: Settings
  }
];

// Time Overview Data
const timeOverview = [
  { step: "1", description: "Anfrage & Termin", duration: "1-2 Tage" },
  { step: "2", description: "Ihre Vorbereitung", duration: "Vor dem Termin" },
  { step: "3", description: "Ankunft & Besprechung", duration: "15 Min" },
  { step: "4", description: "Sichtprüfung", duration: "30-45 Min" },
  { step: "5", description: "VDE-Messungen", duration: "30-45 Min" },
  { step: "6", description: "Inbetriebnahme", duration: "15-30 Min" },
  { step: "7", description: "Dokumentation", duration: "15-20 Min" },
  { step: "8", description: "Einweisung", duration: "20-30 Min" }
];

// FAQ Data
const faqData = [
  {
    question: "Was kostet eine PV-Abnahme?",
    answer: "Die Kosten hängen von der Anlagengröße und dem Aufwand ab. Kontaktieren Sie uns für ein individuelles Angebot."
  },
  {
    question: "Was passiert, wenn Mängel gefunden werden?",
    answer: "Kleinere Mängel können oft direkt vor Ort besprochen und teilweise behoben werden. Bei größeren Mängeln erhalten Sie eine detaillierte Liste mit Lösungsvorschlägen. Nach Behebung kann ein Nachtermin vereinbart werden."
  },
  {
    question: "Muss ich selbst auf das Dach?",
    answer: "Nein, die Dachbegehung übernehmen wir. Sie müssen nur den Zugang ermöglichen (Leiter, Dachfenster o.ä.)."
  },
  {
    question: "Wie schnell bekomme ich einen Termin?",
    answer: "In der Regel innerhalb von 1-2 Wochen. Bei dringenden Fällen versuchen wir, schneller zu reagieren."
  },
  {
    question: "Was passiert bei Stromausfall mit meiner PV-Anlage?",
    answer: "Bei einem Netzausfall schaltet sich Ihre PV-Anlage aus Sicherheitsgründen automatisch ab (Netz- und Anlagenschutz). Dies schützt Personen, die am Stromnetz arbeiten. Mit einem Batteriespeicher und Notstromfunktion können Sie bei Ausfall jedoch weiterhin Strom nutzen."
  },
  {
    question: "Wie oft muss die Anlage gewartet werden?",
    answer: "Eine jährliche Sichtprüfung wird empfohlen – diese können Sie selbst durchführen. Alle 4 Jahre sollte eine Wiederholungsprüfung nach DGUV V3 durch einen Fachbetrieb erfolgen. Bei Auffälligkeiten (Ertragsverlust, Fehlermeldungen) sollten Sie uns kontaktieren."
  },
  {
    question: "Was ist ein Stringplan?",
    answer: "Ein Stringplan zeigt die elektrische Verschaltung Ihrer PV-Module. Er dokumentiert, welche Module in Reihe geschaltet sind (= String) und an welchen Wechselrichter-Eingang sie angeschlossen sind. Der Stringplan ist wichtig für die Fehlersuche und wird bei der Abnahme benötigt."
  },
  {
    question: "Welche Fristen muss ich bei der Anmeldung beachten?",
    answer: "Die Anlage muss innerhalb von 4 Wochen nach Inbetriebnahme im Marktstammdatenregister (MaStR) registriert werden. Die Fertigmeldung beim Netzbetreiber sollte zeitnah nach der Inbetriebnahme erfolgen, damit der Zählertausch und die Einspeisevergütung starten können."
  },
  {
    question: "Brauche ich eine Genehmigung für meine PV-Anlage?",
    answer: "In den meisten Fällen sind Aufdach-Anlagen auf Wohngebäuden genehmigungsfrei. Bei denkmalgeschützten Gebäuden, in Neubaugebieten mit speziellen Vorschriften oder bei Freiflächenanlagen kann eine Genehmigung erforderlich sein. Erkundigen Sie sich im Zweifel bei Ihrer Gemeinde."
  },
  {
    question: "Was ist das Marktstammdatenregister (MaStR)?",
    answer: "Das MaStR ist ein zentrales Online-Register der Bundesnetzagentur, in dem alle Stromerzeugungsanlagen in Deutschland erfasst werden. Die Registrierung ist gesetzlich vorgeschrieben und Voraussetzung für die Einspeisevergütung. Die Anmeldung können Sie selbst online erledigen."
  },
  {
    question: "Wie hoch ist die aktuelle Einspeisevergütung?",
    answer: "Die Einspeisevergütung wird regelmäßig angepasst. Für Anlagen bis 10 kWp liegt sie aktuell bei ca. 8 Cent/kWh (Stand 2024). Die genauen Werte finden Sie auf der Website der Bundesnetzagentur. Eigenverbrauch ist wirtschaftlich meist attraktiver als Volleinspeisung."
  },
  {
    question: "Lohnt sich ein Batteriespeicher?",
    answer: "Ein Speicher erhöht den Eigenverbrauch von ca. 30% auf bis zu 70-80%. Ob sich das wirtschaftlich lohnt, hängt von Ihrem Stromverbrauchsprofil, den Speicherkosten und den Strompreisen ab. Bei hohem Abendverbrauch und steigenden Strompreisen kann sich ein Speicher innerhalb von 10-15 Jahren amortisieren."
  },
  {
    question: "Darf ich meine PV-Anlage selbst installieren?",
    answer: "Die mechanische Montage der Module auf dem Dach dürfen Sie selbst durchführen. Die elektrische Installation (DC-Verkabelung, Wechselrichter-Anschluss, AC-Anschluss ans Hausnetz) muss jedoch von einer Elektrofachkraft ausgeführt oder zumindest abgenommen werden."
  },
  {
    question: "Was muss ein Elektriker machen?",
    answer: "Ein Elektriker muss mindestens: den AC-Anschluss des Wechselrichters ans Hausnetz herstellen, den Zählerschrank vorbereiten, die Schutzeinrichtungen (RCD, Leitungsschutz) installieren und die Anlage nach VDE-Normen prüfen. Ohne diese Arbeiten darf die Anlage nicht in Betrieb genommen werden."
  }
];

// Legal Content
const impressumContent = {
  title: "Impressum",
  content: `
**Angaben gemäß § 5 TMG:**

ENTHIDA – Smart Energy Systems
Elektromeister Enes Dedic

[Straße und Hausnummer]
[PLZ Ort]
Deutschland

**Kontakt:**
Telefon: [Ihre Telefonnummer]
E-Mail: info@enthida.de

**Berufsbezeichnung:**
Elektromeister (verliehen in der Bundesrepublik Deutschland)

**Zuständige Handwerkskammer:**
[Name der Handwerkskammer]
[Adresse]

**Umsatzsteuer-ID:**
Umsatzsteuer-Identifikationsnummer gemäß § 27a Umsatzsteuergesetz:
[Ihre USt-IdNr.]

**Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:**
Enes Dedic
[Adresse]

**Streitschlichtung:**
Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/

Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.

**Haftung für Inhalte:**
Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.

**Haftung für Links:**
Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.

**Urheberrecht:**
Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
  `
};

const datenschutzContent = {
  title: "Datenschutzerklärung",
  content: `
**1. Datenschutz auf einen Blick**

**Allgemeine Hinweise**
Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.

**Datenerfassung auf dieser Website**

*Wer ist verantwortlich für die Datenerfassung auf dieser Website?*
Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.

*Wie erfassen wir Ihre Daten?*
Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben.

Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).

**2. Hosting**

Wir hosten die Inhalte unserer Website bei folgendem Anbieter:

**Externes Hosting**
Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den Servern des Hosters gespeichert.

**3. Allgemeine Hinweise und Pflichtinformationen**

**Datenschutz**
Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.

**Hinweis zur verantwortlichen Stelle**
Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:

ENTHIDA – Smart Energy Systems
Elektromeister Enes Dedic
[Adresse]
E-Mail: info@enthida.de

**Speicherdauer**
Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt wurde, verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck für die Datenverarbeitung entfällt.

**4. Datenerfassung auf dieser Website**

**Cookies**
Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine Datenpakete und richten auf Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.

Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden und Cookies nur im Einzelfall erlauben, die Annahme von Cookies für bestimmte Fälle oder generell ausschließen sowie das automatische Löschen der Cookies beim Schließen des Browsers aktivieren.

**Kontaktformular**
Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.

**5. Ihre Rechte**

Sie haben jederzeit das Recht:
- Auskunft über Ihre bei uns gespeicherten Daten zu erhalten
- Berichtigung unrichtiger Daten zu verlangen
- Löschung Ihrer Daten zu verlangen
- Einschränkung der Verarbeitung zu verlangen
- Datenübertragbarkeit zu verlangen
- Widerspruch gegen die Verarbeitung einzulegen
- Eine erteilte Einwilligung zu widerrufen

Stand: ${new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
  `
};

const cookieContent = {
  title: "Cookie-Richtlinie",
  content: `
**Was sind Cookies?**

Cookies sind kleine Textdateien, die auf Ihrem Computer oder Mobilgerät gespeichert werden, wenn Sie unsere Website besuchen. Sie ermöglichen es der Website, Ihre Aktionen und Präferenzen über einen bestimmten Zeitraum zu speichern, sodass Sie diese nicht jedes Mal neu eingeben müssen, wenn Sie die Website besuchen oder von einer Seite zur anderen wechseln.

**Welche Cookies verwenden wir?**

**Technisch notwendige Cookies**
Diese Cookies sind für den Betrieb der Website unbedingt erforderlich. Sie ermöglichen grundlegende Funktionen wie die Seitennavigation und den Zugang zu geschützten Bereichen der Website. Ohne diese Cookies kann die Website nicht ordnungsgemäß funktionieren.

| Cookie | Zweck | Speicherdauer |
|--------|-------|---------------|
| cookie_consent | Speichert Ihre Cookie-Einstellungen | 1 Jahr |

**Analyse-Cookies (optional)**
Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, indem sie Informationen anonym sammeln und melden.

**Marketing-Cookies (optional)**
Diese Cookies werden verwendet, um Besuchern relevante Werbung und Marketingkampagnen anzuzeigen.

**Wie können Sie Cookies kontrollieren?**

Sie können Ihre Cookie-Einstellungen jederzeit ändern. Die meisten Webbrowser erlauben eine gewisse Kontrolle über die meisten Cookies über die Browser-Einstellungen.

**So löschen Sie Cookies:**
- **Chrome:** Einstellungen → Datenschutz und Sicherheit → Cookies und andere Websitedaten
- **Firefox:** Einstellungen → Datenschutz & Sicherheit → Cookies und Website-Daten
- **Safari:** Einstellungen → Datenschutz → Cookies und Website-Daten verwalten
- **Edge:** Einstellungen → Cookies und Websiteberechtigungen

**Änderungen an dieser Cookie-Richtlinie**

Wir können diese Cookie-Richtlinie von Zeit zu Zeit aktualisieren. Änderungen werden auf dieser Seite veröffentlicht.

**Kontakt**

Bei Fragen zu unserer Cookie-Richtlinie kontaktieren Sie uns bitte unter info@enthida.de.

Stand: ${new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
  `
};

// Navigation Links
const navLinks = [
  { href: "#ablauf", label: "Ablauf" },
  { href: "#zeitplan", label: "Zeitplan" },
  { href: "#danach", label: "Danach" },
  { href: "#faq", label: "FAQ" },
  { href: "#kontakt", label: "Kontakt" }
];

// Header Component
const Header = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  return (
    <header 
      className={`header-nav ${isScrolled ? 'scrolled' : ''}`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="ENTHIDA Logo" 
              className="h-10 sm:h-12 w-auto"
              data-testid="logo-image"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-sm font-medium text-slate-300 hover:text-[#39FF14]"
                data-testid={`nav-link-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu md:hidden" data-testid="mobile-menu">
          <button
            className="absolute top-6 right-6 text-white"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-close"
          >
            <X className="w-8 h-8" />
          </button>
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}
              data-testid={`mobile-nav-link-${link.href.slice(1)}`}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </header>
  );
};

// Hero Section Component
const HeroSection = () => (
  <section className="hero-section print:!block print:!min-h-0 print:!h-auto print:!p-2" data-testid="hero-section">
    <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center print:!p-0">
      <h1 
        className="font-['Sora'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 animate-fade-in-up print:!text-black print:!mb-2"
        data-testid="hero-title"
      >
        Der Ablauf einer{" "}
        <span className="text-gradient">PV-Abnahme</span>
      </h1>
      <p 
        className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto animate-fade-in-up animation-delay-100 print:!text-gray-700"
        data-testid="hero-subtitle"
      >
        Von der ersten Anfrage bis zur Inbetriebnahme – transparent erklärt
      </p>
    </div>
  </section>
);

// Introduction Section
const IntroSection = () => (
  <section className="py-16 sm:py-24 px-4 sm:px-6 print:!py-2" data-testid="intro-section">
    <div className="max-w-3xl mx-auto text-center">
      <p className="text-lg sm:text-xl text-slate-300 leading-relaxed print:!text-gray-700 print:!text-sm">
        Sie fragen sich, was bei einer professionellen PV-Abnahme eigentlich passiert? 
        Hier erklären wir Ihnen den kompletten Ablauf – Schritt für Schritt. 
        So wissen Sie genau, was Sie erwartet und können sich optimal vorbereiten.
      </p>
    </div>
  </section>
);

// Timeline Card Content Component
const TimelineCardContent = ({ step }) => {
  const IconComponent = step.icon;
  
  return (
    <div className="timeline-card" data-testid={`timeline-card-${step.step}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-[#00CED1]/20 rounded-lg">
          <IconComponent className="w-5 h-5 text-[#00CED1]" />
        </div>
        <div>
          <h3 className="font-['Sora'] text-xl font-bold text-white">
            {step.title}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <Clock className="w-4 h-4 text-[#39FF14]" />
            <span className="text-sm text-[#39FF14] font-medium">{step.duration}</span>
          </div>
        </div>
      </div>

      <p className="text-sm font-semibold text-[#00CED1] mb-3">{step.description}</p>

      {/* Regular Points */}
      {step.points && (
        <ul className="space-y-2 mb-4">
          {step.points.map((point, idx) => (
            <li key={idx} className="checklist-item">
              <CheckCircle2 className="checklist-icon" />
              <span className="text-sm text-slate-300">{point}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Sections (for steps 4 and 5) */}
      {step.sections && (
        <div className="space-y-4">
          {step.sections.map((section, idx) => (
            <div key={idx}>
              <h4 className="text-sm font-semibold text-[#00CED1] mb-2">{section.title}</h4>
              <ul className="space-y-1.5 pl-2">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="checklist-item">
                    <CheckCircle2 className="checklist-icon w-4 h-4" />
                    <span className="text-sm text-slate-400">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Requirements Box */}
      {step.requirements && (
        <div className="info-box mt-4">
          <h4 className="text-sm font-semibold text-[#00CED1] mb-2">{step.requirements.title}</h4>
          <ul className="space-y-1">
            {step.requirements.items.map((item, idx) => (
              <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-[#00CED1] flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tip Box */}
      {step.tip && (
        <div className="tip-box mt-4">
          <p className="text-sm text-slate-300">
            <span className="font-semibold text-[#39FF14]">Tipp: </span>
            {step.tip}
          </p>
        </div>
      )}

      {/* Goal/Note/Success */}
      {step.goal && (
        <div className="info-box mt-4">
          <p className="text-sm text-slate-300 italic">"{step.goal}"</p>
        </div>
      )}

      {step.note && (
        <p className="text-sm text-slate-400 mt-4 italic">{step.note}</p>
      )}

      {step.success && (
        <div className="tip-box mt-4">
          <p className="text-sm text-slate-300">
            <span className="font-semibold text-[#39FF14]">Erfolgskriterium: </span>
            "{step.success}"
          </p>
        </div>
      )}

      {/* Purpose Box */}
      {step.purpose && (
        <div className="info-box mt-4">
          <h4 className="text-sm font-semibold text-[#00CED1] mb-2">{step.purpose.title}</h4>
          <ul className="space-y-1">
            {step.purpose.items.map((item, idx) => (
              <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-[#00CED1] flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Storage Info */}
      {step.storage && (
        <div className="info-box mt-4">
          <h4 className="text-sm font-semibold text-[#00CED1] mb-2">{step.storage.title}</h4>
          <ul className="space-y-1">
            {step.storage.items.map((item, idx) => (
              <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-[#00CED1] flex-shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Conclusion */}
      {step.conclusion && (
        <div className="tip-box mt-4">
          <p className="text-sm text-slate-300">
            <span className="font-semibold text-[#39FF14]">Abschluss: </span>
            "{step.conclusion}"
          </p>
        </div>
      )}
    </div>
  );
};

// Timeline Section Component
const TimelineSection = () => (
  <section id="ablauf" className="py-16 sm:py-24 section-dark" data-testid="timeline-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-16">
        <h2 className="font-['Sora'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
          Die <span className="text-gradient">8 Schritte</span> der PV-Abnahme
        </h2>
        <p className="text-slate-400 max-w-2xl mx-auto">
          Vom ersten Kontakt bis zur vollständigen Übergabe – so läuft eine professionelle Abnahme ab.
        </p>
      </div>

      <div className="timeline-container">
        <div className="timeline-line" />
        
        {timelineSteps.map((step) => (
          <div 
            key={step.step} 
            className="timeline-item"
            data-testid={`timeline-step-${step.step}`}
          >
            <div className="timeline-node" data-testid={`timeline-node-${step.step}`}>
              {step.step}
            </div>
            <TimelineCardContent step={step} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

// What Happens After Section
const AfterSection = () => (
  <section id="danach" className="py-16 sm:py-24 section-gradient" data-testid="after-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-12">
        <h2 className="font-['Sora'] text-3xl sm:text-4xl font-bold text-white mb-4">
          Was passiert <span className="text-gradient">danach</span>?
        </h2>
        <p className="text-slate-400 max-w-2xl mx-auto">
          Nach der erfolgreichen Abnahme unterstützen wir Sie bei allen weiteren Schritten – 
          von der Anmeldung bis zur ersten Einspeisevergütung.
        </p>
      </div>

      {/* Main After Steps */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {/* Netzbetreiber */}
        <div className="bento-card" data-testid="after-card-netzbetreiber">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-[#00CED1]/20 rounded-xl">
              <FileCheck className="w-6 h-6 text-[#00CED1]" />
            </div>
            <h3 className="font-['Sora'] text-lg font-bold text-white">Netzbetreiber-Anmeldung</h3>
          </div>
          <ul className="space-y-3">
            <li className="text-sm text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
              Mit dem Inbetriebnahmeprotokoll können Sie (oder wir für Sie) die Anlage beim Netzbetreiber fertigmelden
            </li>
            <li className="text-sm text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
              Der Netzbetreiber schaltet den Einspeisezähler frei oder tauscht ihn aus
            </li>
            <li className="text-sm text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
              Ab jetzt erhalten Sie Einspeisevergütung
            </li>
          </ul>
        </div>

        {/* Marktstammdatenregister */}
        <div className="bento-card" data-testid="after-card-marktstammdaten">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-[#39FF14]/20 rounded-xl">
              <ExternalLink className="w-6 h-6 text-[#39FF14]" />
            </div>
            <h3 className="font-['Sora'] text-lg font-bold text-white">Marktstammdatenregister</h3>
          </div>
          <ul className="space-y-3">
            <li className="text-sm text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
              Innerhalb von 4 Wochen nach Inbetriebnahme muss die Anlage registriert werden
            </li>
            <li className="text-sm text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
              Das können Sie selbst online erledigen – wir helfen gerne bei Fragen
            </li>
            <li className="text-sm text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
              Ohne Registrierung keine Einspeisevergütung!
            </li>
          </ul>
        </div>

        {/* Langfristig */}
        <div className="bento-card" data-testid="after-card-langfristig">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-[#00CED1]/20 rounded-xl">
              <Shield className="w-6 h-6 text-[#00CED1]" />
            </div>
            <h3 className="font-['Sora'] text-lg font-bold text-white">Langfristig</h3>
          </div>
          <ul className="space-y-3">
            <li className="text-sm text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
              Jährliche Sichtprüfung empfohlen (können Sie selbst machen)
            </li>
            <li className="text-sm text-slate-300 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
              Alle 4 Jahre: Wiederholungsprüfung nach DGUV V3 (das machen wir gerne für Sie)
            </li>
          </ul>
        </div>
      </div>

      {/* New Section: Netzanschluss & Anmeldung Details */}
      <div className="mt-16">
        <div className="text-center mb-10">
          <h3 className="font-['Sora'] text-2xl sm:text-3xl font-bold text-white mb-3">
            <span className="text-gradient">Netzanschluss</span> & Anmeldung im Detail
          </h3>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Wir prüfen vorab, ob Ihr Zählerschrank bereit für den Netzbetreiber ist. 
            Schicken Sie uns Fotos – wir sagen Ihnen, ob alles passt oder was noch angepasst werden muss.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Zählerschrank-Vorbereitung */}
          <div className="glass-card p-6" data-testid="netzanschluss-zaehler">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-[#00CED1]/20 rounded-xl">
                <Settings className="w-6 h-6 text-[#00CED1]" />
              </div>
              <h4 className="font-['Sora'] text-lg font-bold text-white">Zählerschrank & Zählerwechsel</h4>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Für die Einspeisung benötigen Sie einen Zweirichtungszähler. Der Netzbetreiber tauscht Ihren alten Zähler aus – 
              aber nur wenn der Zählerschrank normkonform vorbereitet ist.
            </p>
            <div className="space-y-2">
              <p className="text-sm text-[#00CED1] font-semibold">Was wir prüfen:</p>
              <ul className="space-y-2">
                <li className="text-sm text-slate-400 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  Zählerplatz nach aktueller TAB (Technische Anschlussbedingungen)
                </li>
                <li className="text-sm text-slate-400 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  Ausreichend Platz für Zweirichtungszähler
                </li>
                <li className="text-sm text-slate-400 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  Korrekte Absicherung und Verdrahtung
                </li>
                <li className="text-sm text-slate-400 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  SLS-Schalter vorhanden (falls erforderlich)
                </li>
              </ul>
            </div>
            <div className="tip-box mt-4">
              <p className="text-sm text-slate-300">
                <span className="font-semibold text-[#39FF14]">Tipp: </span>
                Schicken Sie uns vorab Fotos Ihres Zählerschranks per WhatsApp – wir prüfen kostenlos, ob alles passt!
              </p>
            </div>
          </div>

          {/* TAB & Netzbetreiber */}
          <div className="glass-card p-6" data-testid="netzanschluss-tab">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-[#39FF14]/20 rounded-xl">
                <FileText className="w-6 h-6 text-[#39FF14]" />
              </div>
              <h4 className="font-['Sora'] text-lg font-bold text-white">Technische Anschlussbedingungen (TAB)</h4>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Jeder Netzbetreiber hat eigene technische Anforderungen. Wir kennen die TAB der regionalen Netzbetreiber 
              und stellen sicher, dass Ihre Anlage alle Vorgaben erfüllt.
            </p>
            <div className="space-y-2">
              <p className="text-sm text-[#00CED1] font-semibold">Wichtige Anforderungen:</p>
              <ul className="space-y-2">
                <li className="text-sm text-slate-400 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  NA-Schutz (Netz- und Anlagenschutz) im Wechselrichter
                </li>
                <li className="text-sm text-slate-400 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  Einspeisemanagement bei Anlagen über 25 kWp
                </li>
                <li className="text-sm text-slate-400 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  Korrekte Anmeldung vor Inbetriebnahme
                </li>
                <li className="text-sm text-slate-400 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  Inbetriebnahmeprotokoll nach VDE-AR-N 4105
                </li>
              </ul>
            </div>
          </div>

          {/* Einspeisevergütung */}
          <div className="glass-card p-6" data-testid="netzanschluss-verguetung">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-[#00CED1]/20 rounded-xl">
                <Zap className="w-6 h-6 text-[#00CED1]" />
              </div>
              <h4 className="font-['Sora'] text-lg font-bold text-white">Einspeisevergütung</h4>
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Für jede eingespeiste Kilowattstunde erhalten Sie eine gesetzlich garantierte Vergütung über 20 Jahre.
            </p>
            <div className="info-box">
              <p className="text-sm text-slate-300 mb-2">
                <span className="font-semibold text-[#00CED1]">Aktuelle Vergütungssätze (2024):</span>
              </p>
              <ul className="space-y-1 text-sm text-slate-400">
                <li>• Anlagen bis 10 kWp: ca. 8,1 Cent/kWh</li>
                <li>• Anlagen 10-40 kWp: ca. 7,0 Cent/kWh</li>
                <li>• Volleinspeisung: höhere Vergütung möglich</li>
              </ul>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Die Vergütung wird ab dem Tag der Inbetriebnahme berechnet – daher ist eine zügige Anmeldung wichtig!
            </p>
          </div>

          {/* Ablauf & Fristen */}
          <div className="glass-card p-6" data-testid="netzanschluss-fristen">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-[#39FF14]/20 rounded-xl">
                <Clock className="w-6 h-6 text-[#39FF14]" />
              </div>
              <h4 className="font-['Sora'] text-lg font-bold text-white">Ablauf & Fristen</h4>
            </div>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#39FF14]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#39FF14] font-bold text-sm">1</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Vor der Installation</p>
                  <p className="text-xs text-slate-400">Netzanschlussbegehren beim Netzbetreiber einreichen</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#39FF14]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#39FF14] font-bold text-sm">2</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Nach der Abnahme</p>
                  <p className="text-xs text-slate-400">Fertigmeldung mit Inbetriebnahmeprotokoll einreichen</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#39FF14]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#39FF14] font-bold text-sm">3</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Innerhalb 4 Wochen</p>
                  <p className="text-xs text-slate-400">Registrierung im Marktstammdatenregister (MaStR)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#00CED1]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#00CED1] font-bold text-sm">✓</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Zählertausch</p>
                  <p className="text-xs text-slate-400">Netzbetreiber setzt Zweirichtungszähler – Einspeisung startet!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Time Overview Table Section
const TimeOverviewSection = () => (
  <section id="zeitplan" className="py-16 sm:py-24 section-dark" data-testid="time-overview-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-12">
        <h2 className="font-['Sora'] text-3xl sm:text-4xl font-bold text-white mb-4">
          <span className="text-gradient">Zeitübersicht</span>
        </h2>
        <p className="text-slate-400">
          Planen Sie ca. 2-3 Stunden für den Vor-Ort-Termin ein
        </p>
      </div>

      <div className="time-table" data-testid="time-overview-table">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#00CED1]/20 hover:bg-transparent">
              <TableHead className="text-[#00CED1] font-['Sora'] font-semibold">Schritt</TableHead>
              <TableHead className="text-[#00CED1] font-['Sora'] font-semibold">Beschreibung</TableHead>
              <TableHead className="text-[#00CED1] font-['Sora'] font-semibold text-right">Dauer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {timeOverview.map((row) => (
              <TableRow 
                key={row.step} 
                className="border-b border-[#00CED1]/10 hover:bg-[#00CED1]/5"
                data-testid={`time-row-${row.step}`}
              >
                <TableCell className="font-semibold text-[#39FF14]">{row.step}</TableCell>
                <TableCell className="text-slate-300">{row.description}</TableCell>
                <TableCell className="text-right text-slate-400">{row.duration}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-[#00CED1]/10 hover:bg-[#00CED1]/15">
              <TableCell className="font-bold text-white" colSpan={2}>Gesamt vor Ort</TableCell>
              <TableCell className="text-right font-bold text-[#39FF14]">ca. 2-3 Stunden</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  </section>
);

// FAQ Section
const FAQSection = () => (
  <section id="faq" className="py-16 sm:py-24 section-gradient" data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-12 print:mb-4">
        <h2 className="font-['Sora'] text-3xl sm:text-4xl font-bold text-white mb-4 print:!text-black print:!text-lg">
          Häufige <span className="text-gradient">Fragen</span>
        </h2>
      </div>

      {/* Screen version - Accordion */}
      <div className="print:hidden">
        <Accordion type="single" collapsible className="faq-accordion space-y-4" data-testid="faq-accordion">
          {faqData.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="border border-[#00CED1]/20 rounded-xl px-6 bg-[#0a0f1a]/60 backdrop-blur-sm"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger 
                className="text-left text-white hover:text-[#39FF14] hover:no-underline py-5 font-['Sora'] font-semibold"
                data-testid={`faq-trigger-${index}`}
              >
                {faq.question}
              </AccordionTrigger>
              <AccordionContent 
                className="text-slate-300 pb-5"
                data-testid={`faq-content-${index}`}
              >
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Print version - Static list */}
      <div className="hidden print:block">
        {faqData.map((faq, index) => (
          <div key={index} className="mb-3 p-2 border border-gray-400 rounded" style={{ backgroundColor: '#f8f8f8' }}>
            <p className="font-bold text-black text-sm mb-1">{faq.question}</p>
            <p className="text-gray-700 text-sm">{faq.answer}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section id="kontakt" className="cta-section py-20 sm:py-32 relative border-t border-[#00CED1]/20 bg-gradient-to-b from-transparent to-[#00CED1]/5" data-testid="cta-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
      <h2 className="font-['Sora'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
        Bereit für Ihre <span className="text-gradient">Abnahme</span>?
      </h2>
      <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
        Kontaktieren Sie uns für einen Termin – wir freuen uns auf Ihre Anfrage!
      </p>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
        <a
          href="https://wa.me/4917641234614"
          target="_blank"
          rel="noopener noreferrer"
          className="cta-btn-whatsapp flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg w-full sm:w-auto justify-center"
          data-testid="cta-whatsapp"
        >
          <MessageCircle className="w-5 h-5" />
          WhatsApp
        </a>
        <a
          href="tel:+4917641234614"
          className="cta-btn-call flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg w-full sm:w-auto justify-center"
          data-testid="cta-phone"
        >
          <Phone className="w-5 h-5" />
          Anrufen
        </a>
        <a
          href="mailto:info@enthida.de"
          className="cta-btn-email flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg w-full sm:w-auto justify-center"
          data-testid="cta-email"
        >
          <Mail className="w-5 h-5" />
          E-Mail
        </a>
      </div>
    </div>
  </section>
);

// Footer Component
const Footer = ({ onOpenModal }) => (
  <footer className="border-t border-[#00CED1]/20 py-12 bg-[#05080f]" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex flex-col items-center md:items-start gap-4">
          <img 
            src={LOGO_URL} 
            alt="ENTHIDA Logo" 
            className="h-12 w-auto"
            data-testid="footer-logo"
          />
          <div className="text-center md:text-left">
            <p className="text-slate-400 text-sm">ENTHIDA – Smart Energy Systems</p>
            <p className="text-slate-500 text-sm">Elektromeister Enes Dedic</p>
          </div>
        </div>

        <div className="text-center md:text-right">
          <p className="text-slate-400 text-sm">
            Einsatzgebiet: Eggenfelden + 100km / Bayern
          </p>
        </div>
      </div>

      {/* Legal Links */}
      <div className="mt-8 pt-8 border-t border-[#00CED1]/10">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-4">
          <button
            onClick={() => onOpenModal('impressum')}
            className="text-slate-400 text-sm hover:text-[#00CED1] transition-colors"
            data-testid="footer-impressum"
          >
            Impressum
          </button>
          <button
            onClick={() => onOpenModal('datenschutz')}
            className="text-slate-400 text-sm hover:text-[#00CED1] transition-colors"
            data-testid="footer-datenschutz"
          >
            Datenschutz
          </button>
          <button
            onClick={() => onOpenModal('cookies')}
            className="text-slate-400 text-sm hover:text-[#00CED1] transition-colors"
            data-testid="footer-cookies"
          >
            Cookie-Richtlinie
          </button>
        </div>
        <p className="text-slate-600 text-xs text-center">
          © {new Date().getFullYear()} ENTHIDA. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  </footer>
);

// Legal Modal Component
const LegalModal = ({ isOpen, onClose, content }) => {
  if (!content) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-[#0a0f1a] border border-[#00CED1]/30 text-white">
        <DialogHeader>
          <DialogTitle className="font-['Sora'] text-2xl text-white">{content.title}</DialogTitle>
        </DialogHeader>
        <div className="prose prose-invert prose-sm max-w-none mt-4">
          <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">
            {content.content.split('\n').map((line, idx) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                return <h3 key={idx} className="text-[#00CED1] font-semibold mt-4 mb-2">{line.replace(/\*\*/g, '')}</h3>;
              }
              if (line.startsWith('*') && line.endsWith('*')) {
                return <p key={idx} className="text-slate-400 italic">{line.replace(/\*/g, '')}</p>;
              }
              if (line.startsWith('|')) {
                return null; // Skip table rows, we'd need more complex parsing
              }
              return <p key={idx} className="mb-2">{line}</p>;
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Cookie Consent Banner Component
const CookieBanner = ({ onAccept, onDecline }) => (
  <div 
    className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-[#0a0f1a]/95 backdrop-blur-lg border-t border-[#00CED1]/30 print:hidden"
    data-testid="cookie-banner"
  >
    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-start gap-3">
        <Cookie className="w-6 h-6 text-[#00CED1] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-white text-sm font-medium mb-1">Cookie-Einstellungen</p>
          <p className="text-slate-400 text-xs">
            Wir verwenden Cookies, um Ihnen das beste Erlebnis auf unserer Website zu bieten. 
            Sie können wählen, welche Cookies Sie akzeptieren möchten.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={onDecline}
          className="px-4 py-2 text-sm text-slate-300 hover:text-white border border-slate-600 rounded-lg hover:border-slate-500 transition-colors"
          data-testid="cookie-decline"
        >
          Nur notwendige
        </button>
        <button
          onClick={onAccept}
          className="px-4 py-2 text-sm bg-[#00CED1] text-black font-medium rounded-lg hover:bg-[#00b3b3] transition-colors"
          data-testid="cookie-accept"
        >
          Alle akzeptieren
        </button>
      </div>
    </div>
  </div>
);

// PDF Print Button Component
const PDFPrintButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 bg-[#39FF14] text-black font-semibold rounded-xl shadow-lg hover:bg-[#32e612] transition-all hover:scale-105 print:hidden"
    data-testid="pdf-print-button"
  >
    <FileDown className="w-5 h-5" />
    Als PDF drucken
  </button>
);

// Print Hint Modal Component
const PrintHintModal = ({ isOpen, onConfirm, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0a0f1a] border border-[#00CED1]/30 text-white">
        <DialogHeader>
          <DialogTitle className="font-['Sora'] text-xl text-white flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-[#39FF14]" />
            Hinweis zum PDF-Druck
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <p className="text-slate-300 text-sm leading-relaxed mb-6">
            Die Druckvorschau funktioniert am besten mit dem <strong className="text-[#00CED1]">Firefox-Browser</strong>.
            <br /><br />
            Wählen Sie im Druckdialog "Als PDF speichern" als Drucker aus.
          </p>
          <button
            onClick={onConfirm}
            className="w-full px-6 py-3 bg-[#39FF14] text-black font-semibold rounded-xl hover:bg-[#32e612] transition-colors"
            data-testid="print-confirm-button"
          >
            Verstanden – weiter zum Druck
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Print Contact Info (visible only in print)
const PrintContactInfo = () => (
  <div className="hidden print:block print-contact-info">
    <div className="border-t-2 border-black pt-4 mt-4">
      <h3 className="font-bold text-lg mb-2">Kontakt</h3>
      <p className="text-sm">ENTHIDA – Smart Energy Systems</p>
      <p className="text-sm">Elektromeister Enes Dedic</p>
      <p className="text-sm">Einsatzgebiet: Eggenfelden + 100km / Bayern</p>
      <p className="text-sm mt-2">Telefon: +49 176 41234 614</p>
      <p className="text-sm">E-Mail: info@enthida.de</p>
    </div>
  </div>
);

// Print Watermark (visible only in print - fixed position shows on all pages)
const PrintWatermark = () => (
  <div className="hidden print:block print-watermark" aria-hidden="true">
    <img 
      src={LOGO_URL} 
      alt="" 
    />
  </div>
);

// Print FAQ Section (visible only in print - shows all answers)
const PrintFAQSection = () => (
  <div className="hidden print:block mt-4">
    <h2 className="font-bold text-lg mb-3">Häufige Fragen</h2>
    {faqData.map((faq, index) => (
      <div key={index} className="mb-3 p-2 border border-gray-400 rounded bg-gray-50">
        <p className="font-bold text-sm mb-1">{faq.question}</p>
        <p className="text-sm text-gray-700">{faq.answer}</p>
      </div>
    ))}
  </div>
);

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });
  const [showPrintHint, setShowPrintHint] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Check cookie consent on mount
  useEffect(() => {
    const cookieConsent = localStorage.getItem('enthida_cookie_consent');
    if (!cookieConsent) {
      setShowCookieBanner(true);
    }
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCookieAccept = () => {
    localStorage.setItem('enthida_cookie_consent', 'all');
    setShowCookieBanner(false);
  };

  const handleCookieDecline = () => {
    localStorage.setItem('enthida_cookie_consent', 'necessary');
    setShowCookieBanner(false);
  };

  const openLegalModal = (type) => {
    setLegalModal({ isOpen: true, type });
  };

  const closeLegalModal = () => {
    setLegalModal({ isOpen: false, type: null });
  };

  const getLegalContent = () => {
    switch (legalModal.type) {
      case 'impressum':
        return impressumContent;
      case 'datenschutz':
        return datenschutzContent;
      case 'cookies':
        return cookieContent;
      default:
        return null;
    }
  };

  const handlePrintClick = () => {
    setShowPrintHint(true);
  };

  const handlePrintConfirm = () => {
    setShowPrintHint(false);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] print:min-h-0 print:bg-white" data-testid="app-container">
      {/* Print Watermark */}
      <PrintWatermark />
      
      <Header 
        isScrolled={isScrolled} 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      
      <main>
        {/* Print flows naturally without forced page breaks */}
        <HeroSection />
        <IntroSection />
        <TimelineSection />
        
        {/* Page break before time overview */}
        <div className="print-page-break">
          <TimeOverviewSection />
          <AfterSection />
          <FAQSection />
          <PrintContactInfo />
        </div>
        
        {/* CTA - only on screen */}
        <div className="print:hidden">
          <CTASection />
        </div>
      </main>

      <Footer onOpenModal={openLegalModal} />

      {/* PDF Print Button */}
      <PDFPrintButton onClick={handlePrintClick} />

      {/* Print Hint Modal */}
      <PrintHintModal 
        isOpen={showPrintHint} 
        onConfirm={handlePrintConfirm}
        onClose={() => setShowPrintHint(false)}
      />

      {/* Cookie Banner */}
      {showCookieBanner && (
        <CookieBanner 
          onAccept={handleCookieAccept} 
          onDecline={handleCookieDecline} 
        />
      )}

      {/* Legal Modals */}
      <LegalModal 
        isOpen={legalModal.isOpen} 
        onClose={closeLegalModal} 
        content={getLegalContent()} 
      />
    </div>
  );
}

export default App;
